import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PenLine, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FirmaCarta() {
  const [count, setCount] = useState(null);
  const [form, setForm] = useState({ nombre: "", email: "", provincia: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/signatures/count`)
      .then(({ data }) => setCount(data.count))
      .catch(() => setCount(0));
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      toast.error("Indica tu nombre para firmar.");
      return;
    }
    if (!consent) {
      toast.error("Debes aceptar la Política de Privacidad para firmar.");
      return;
    }
    setLoading(true);
    try {
      const payload = { nombre: form.nombre.trim() };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.provincia.trim()) payload.provincia = form.provincia.trim();
      const { data } = await axios.post(`${API}/sign`, payload);
      setCount(data.count);
      setSigned(true);
      toast.success("¡Gracias por firmar la Carta de Responsabilidad!");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("Ya habías firmado con este correo. ¡Gracias!");
        setSigned(true);
      } else {
        toast.error("No se pudo registrar tu firma. Inténtalo de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="firma" className="py-20 lg:py-28 bg-white border-b border-gray-200">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-espana-red mb-3">
            Súmate al compromiso
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
            Firma la Carta de Responsabilidad.
          </h2>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed">
            «Quien incumple, dimite.» Firma para respaldar públicamente un
            compromiso de responsabilidad, transparencia y ejemplaridad superior al
            que exige la ley.
          </p>
          <div className="mt-8 flex items-center gap-4">
            <div className="text-5xl lg:text-6xl font-black tracking-tighter text-espana-red">
              {count === null ? "—" : count.toLocaleString("es-ES")}
            </div>
            <div className="text-sm font-bold uppercase tracking-wide text-gray-500">
              firmas de
              <br />
              ciudadanos
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-[#FAFAFA] border border-gray-200 p-6 sm:p-8"
        >
          {signed ? (
            <div className="text-center py-8" data-testid="firma-gracias">
              <CheckCircle2 size={48} className="text-espana-red mx-auto mb-4" />
              <h3 className="text-xl font-bold">Firma registrada</h3>
              <p className="mt-2 text-gray-600">
                Gracias por sumarte. Comparte la Carta para que más gente la firme.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="firma-form" className="space-y-4">
              <div>
                <label className="block text-sm font-bold mb-2">Nombre y apellidos</label>
                <input
                  name="nombre"
                  value={form.nombre}
                  onChange={onChange}
                  data-testid="firma-nombre"
                  className="w-full border border-gray-300 rounded-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-espana-red focus:border-espana-red transition-colors"
                  placeholder="Tu nombre"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-2">Email (opcional)</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid="firma-email"
                    className="w-full border border-gray-300 rounded-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-espana-red focus:border-espana-red transition-colors"
                    placeholder="tu@correo.es"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-2">Provincia (opcional)</label>
                  <input
                    name="provincia"
                    value={form.provincia}
                    onChange={onChange}
                    data-testid="firma-provincia"
                    className="w-full border border-gray-300 rounded-sm px-4 py-3 focus:outline-none focus:ring-1 focus:ring-espana-red focus:border-espana-red transition-colors"
                    placeholder="Ej. Madrid"
                  />
                </div>
              </div>
              <label className="flex items-start gap-2.5 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="firma-consent"
                  className="mt-0.5 h-4 w-4 accent-espana-red shrink-0"
                />
                <span>
                  Acepto la{" "}
                  <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-espana-red font-bold underline">
                    Política de Privacidad
                  </a>{" "}
                  y que se registre mi firma.
                </span>
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="firma-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-espana-red hover:bg-espana-reddark disabled:opacity-60 text-white font-bold px-7 py-3.5 rounded-sm transition-colors"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <PenLine size={18} />}
                Firmar la Carta
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
